import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import { SALES_RULES_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import SalesRuleDetails from 'Services/ProductManagement/Features/SalesRule/Components/SalesRuleDetails'
import SalesRuleList from 'Services/ProductManagement/Features/SalesRule/Components/SalesRuleList'
import SalesRuleProductsEdit from 'Services/ProductManagement/Features/SalesRule/Components/SalesRuleProducts'
import SalesRuleZoneRequirements from 'Services/ProductManagement/Features/SalesRule/Components/SalesRuleZoneRequirements'
import { useSalesRulesWithParams } from 'Services/ProductManagement/Features/SalesRule/Hooks'
import {
  useDeleteSingleSalesRuleMutation,
  useGetSingleSalesRuleQuery
} from 'Services/ProductManagement/Features/SalesRule/salesRuleApi'
import { SalesRuleTabType } from 'Services/ProductManagement/Features/SalesRule/types'
import { PRODUCT_MANAGEMENT_ROOT_URL, ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export default function SalesRuleListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/sales-rules`
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const salesRuleDrawerTabFromParams =
    searchParams.get(SALES_RULES_TAB_PARAM_KEY) ?? SalesRuleTabType.DETAILS

  const breadcrumbs = [{ title: t`Settings` }, { title: t`Sales rules`, link: baseUrl }]

  const {
    salesRules,
    loading: isLoading,
    totalResults,
    searchQueryParams,
    setSearchQueryParams,
    selectedSalesRuleId,
    setSelectedSalesRuleId
  } = useSalesRulesWithParams()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedSalesRuleId)
  const { data: singeSalesRule = null, isLoading: singleLoading } = useGetSingleSalesRuleQuery(
    selectedSalesRuleId as string,
    {
      skip: !selectedSalesRuleId
    }
  )
  const [deleteSingleSalesRule, { isLoading: isDeleteLoading }] = useDeleteSingleSalesRuleMutation()

  const { handleChangePage, handleRequestSort } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const handleRowClick = (id: string) => {
    setSelectedSalesRuleId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setSelectedSalesRuleId('')
    searchParams.delete(SALES_RULES_TAB_PARAM_KEY)
    setSearchParams(searchParams)
    setIsDrawerOpen(false)
  }

  const handleSalesRuleDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: SalesRuleTabType
  ) => {
    searchParams.set(SALES_RULES_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleProductsEditDrawerClose = () => {
    searchParams.delete(SALES_RULES_TAB_PARAM_KEY)
    setSearchParams(searchParams)
    setIsDrawerOpen(false)
  }

  const handleDelete = () => {
    if (!selectedSalesRuleId) return

    deleteSingleSalesRule(selectedSalesRuleId)
      .then(() => {
        setSelectedSalesRuleId('')
        setIsDrawerOpen(false)
        SnackbarUtils.success(t`Sales rule successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the sales rule.`)
      })
  }

  const isSingleSalesRuleLoading = singleLoading || isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Sales rules`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/create`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Create new sales rule`}
        </CustomButton>
      </ListActions>
      <Box pt={1}>
        <SalesRuleList
          rows={salesRules}
          isLoading={isLoading}
          params={searchQueryParams}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          count={totalResults}
          page={
            searchQueryParams.start_index
              ? Number.parseInt(searchQueryParams.start_index) / ROWS_PER_PAGE_DEFAULT
              : 0
          }
          onRowClick={handleRowClick}
          selectedSalesRuleId={selectedSalesRuleId}
        />
      </Box>
      <DrawerRight
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        topBar={
          <Box>
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the sales rule?`,
                  handleConfirm: () => {
                    handleDelete()
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/${selectedSalesRuleId}/edit`)}
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          </Box>
        }
      >
        <WithLoader loading={isSingleSalesRuleLoading}>
          {singeSalesRule && (
            <TabContext value={salesRuleDrawerTabFromParams}>
              <TabList
                onChange={handleSalesRuleDrawerTabChange}
                aria-label={t`Sales rule's tab list`}
              >
                <Tab label={t`Details`} value={SalesRuleTabType.DETAILS} />
                <Tab label={t`Products`} value={SalesRuleTabType.PRODUCTS} />
                <Tab label={t`Zone requirements`} value={SalesRuleTabType.ZONE_REQUIREMENTS} />
              </TabList>
              <Divider />
              <TabPanel sx={{ p: 0 }} value={SalesRuleTabType.DETAILS}>
                <SalesRuleDetails salesRule={singeSalesRule} />
              </TabPanel>
              <TabPanel sx={{ p: 0 }} value={SalesRuleTabType.PRODUCTS}>
                <SalesRuleProductsEdit
                  salesRule={singeSalesRule}
                  handleCancel={handleProductsEditDrawerClose}
                />
              </TabPanel>
              <TabPanel sx={{ p: 0 }} value={SalesRuleTabType.ZONE_REQUIREMENTS}>
                <SalesRuleZoneRequirements salesRule={singeSalesRule} />
              </TabPanel>
            </TabContext>
          )}
        </WithLoader>
      </DrawerRight>
    </Box>
  )
}
